import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRolById, createRol, updateRol } from '../services/rolService';
import { getPermisos } from '../services/permisoService';
import { ArrowLeft, Save, Shield, CheckCircle2, Search } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';

export default function RolForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [permisos, setPermisos] = useState([]);
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        permisoIds: []
    });
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const cargarDatos = async () => {
            try {
                setLoading(true);
                const [listaPermisos, rolData] = await Promise.all([
                    getPermisos(),
                    isEditing ? getRolById(id) : Promise.resolve(null)
                ]);

                setPermisos(listaPermisos);
                if (rolData) {
                    setFormData({
                        nombre: rolData.nombre || '',
                        descripcion: rolData.descripcion || '',
                        permisoIds: rolData.permisoIds || []
                    });
                }
            } catch (error) {
                console.error('Error al cargar datos:', error);
                toast.error('Error al cargar la información');
            } finally {
                setLoading(false);
            }
        };

        cargarDatos();
    }, [id, isEditing]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleTogglePermiso = (permisoId) => {
        setFormData(prev => {
            const exists = prev.permisoIds.includes(permisoId);
            if (exists) {
                return { ...prev, permisoIds: prev.permisoIds.filter(pid => pid !== permisoId) };
            } else {
                return { ...prev, permisoIds: [...prev.permisoIds, permisoId] };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombre) {
            toast.error('El nombre del rol es obligatorio');
            return;
        }

        try {
            setLoading(true);
            if (isEditing) {
                await updateRol(id, formData);
                toast.success('Rol actualizado exitosamente');
            } else {
                await createRol(formData);
                toast.success('Rol creado exitosamente');
            }
            setTimeout(() => navigate('/seguridad/roles'), 1000);
        } catch (error) {
            console.error('Error al guardar:', error);
            toast.error(error.message || 'Error al guardar el rol');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto font-sans">
            <Toaster position="top-right" />

            <div className="flex items-center justify-between mb-6 bg-white p-4 shadow-sm border border-gray-100 rounded-lg">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/seguridad/roles')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-gray-800">
                            {isEditing ? 'Editar Rol' : 'Nuevo Rol'}
                        </h1>
                        <p className="text-sm text-gray-500">Define los permisos para este perfil de usuario</p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Columna Izquierda: Datos Básicos */}
                <div className="md:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                            <Shield className="w-5 h-5 text-blue-600" />
                            <h2 className="font-bold text-gray-700 uppercase text-sm tracking-wider">Información General</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nombre Técnico</label>
                                <input
                                    type="text"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    placeholder="Ej: ROLE_ADMIN, ROLE_CAJERO"
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm font-mono"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Descripción</label>
                                <textarea
                                    name="descripcion"
                                    value={formData.descripcion}
                                    onChange={handleChange}
                                    placeholder="Explica qué funciones cumple este rol..."
                                    rows="4"
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                />
                            </div>
                        </div>

                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition-all disabled:opacity-50 shadow-md"
                            >
                                <Save className="w-4 h-4" />
                                {loading ? 'GUARDANDO...' : 'GUARDAR ROL'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Columna Derecha: Selección de Permisos */}
                <div className="md:col-span-2">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 h-full">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                                <h2 className="font-bold text-gray-700 uppercase text-sm tracking-wider">Asignación de Permisos</h2>
                            </div>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-bold">
                                {formData.permisoIds.length} seleccionados
                            </span>
                        </div>

                        {/* Buscador de Permisos */}
                        <div className="relative mb-4">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Buscar coincidencias de permisos (nombre o descripción)..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all"
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                            {permisos
                                .filter(p => 
                                    p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                    (p.descripcion && p.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
                                )
                                .map(permiso => (
                                    <div
                                        key={permiso.id}
                                        onClick={() => handleTogglePermiso(permiso.id)}
                                        className={`p-3 border rounded cursor-pointer transition-all flex items-start gap-3 group ${formData.permisoIds.includes(permiso.id)
                                            ? 'border-blue-500 bg-blue-50 shadow-sm'
                                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className={`mt-0.5 w-4 h-4 rounded-sm border flex items-center justify-center transition-colors ${formData.permisoIds.includes(permiso.id)
                                            ? 'bg-blue-600 border-blue-600'
                                            : 'bg-white border-gray-300 group-hover:border-blue-400'
                                            }`}>
                                            {formData.permisoIds.includes(permiso.id) && (
                                                <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                                            )}
                                        </div>
                                        <div>
                                            <div className="text-xs font-bold text-gray-800 font-mono tracking-tight uppercase">
                                                {permiso.nombre}
                                            </div>
                                            <p className="text-[0.65rem] text-gray-500 leading-tight mt-0.5">
                                                {permiso.descripcion || 'Sin descripción detallada'}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
